import type { IconType } from "react-icons";
import {
  FiArchive,
  FiAward,
  FiBarChart2,
  FiBriefcase,
  FiCheckCircle,
  FiEdit,
  FiFileText,
  FiFolder,
  FiGlobe,
  FiHome,
  FiInbox,
  FiList,
  FiPlusCircle,
  FiSearch,
  FiShoppingCart,
} from "react-icons/fi";
import { urls } from "../../../urls";
import { type BucketBadgeKey, bucketQuery } from "./statusBuckets";

export type AppRole = "advisor" | "orderer";

// Needs (behov*) badges plus the procurement bucket badges. Bucket badges are owned by
// statusBuckets so BUCKET_META can't assign a Needs key to a bucket.
export type NavBadgeKey = "behovInnmeldt" | "behovGodkjent" | BucketBadgeKey;

export interface NavItem {
  icon: IconType;
  labelKey: string;
  path: string;
  query?: string;
  roles: AppRole[];
  badgeKey?: NavBadgeKey;
}

export type SectionActionKind = "createSourcingEvent" | "createContract";

export interface NavSectionAction {
  labelKey: string;
  roles: AppRole[];
  kind: SectionActionKind;
}

export interface NavSection {
  titleKey: string;
  items: NavItem[];
  action?: NavSectionAction;
}

const ALL_ROLES: AppRole[] = ["advisor", "orderer"];

export const navConfig: NavSection[] = [
  {
    titleKey: "Overview",
    items: [{ icon: FiHome, labelKey: "Overview", path: urls.v2.home, roles: ALL_ROLES }],
  },
  {
    titleKey: "Needs",
    items: [
      { icon: FiPlusCircle, labelKey: "New need", path: urls.v2.needs.new, roles: ALL_ROLES },
      { icon: FiInbox, labelKey: "Inbox", path: urls.v2.needs.list, query: "status=REPORTED", roles: ["advisor"], badgeKey: "behovInnmeldt" },
      { icon: FiList, labelKey: "My needs", path: urls.v2.needs.mine, roles: ALL_ROLES },
      { icon: FiInbox, labelKey: "Needs overview", path: urls.v2.needs.list, roles: ["advisor"] },
      { icon: FiCheckCircle, labelKey: "Approved needs", path: urls.v2.needs.list, query: "status=APPROVED", roles: ["advisor"], badgeKey: "behovGodkjent" },
    ],
  },
  {
    titleKey: "Market",
    items: [
      { icon: FiSearch, labelKey: "Market surveys", path: urls.v2.marketSurveys.list, roles: ["advisor"] },
      { icon: FiBriefcase, labelKey: "Companies", path: urls.v2.organizations, roles: ALL_ROLES },
    ],
  },
  {
    titleKey: "Procurements",
    items: [
      { icon: FiShoppingCart, labelKey: "All procurements", path: urls.v2.sourcingEvents.viewAll, roles: ALL_ROLES },
      { icon: FiEdit, labelKey: "In progress", path: urls.v2.sourcingEvents.viewAll, query: bucketQuery("arbeid"), roles: ["advisor"], badgeKey: "anskArbeid" },
      { icon: FiGlobe, labelKey: "Announced", path: urls.v2.sourcingEvents.viewAll, query: bucketQuery("kunngjort"), roles: ["advisor"], badgeKey: "anskKunngjort" },
      { icon: FiBarChart2, labelKey: "Under evaluation", path: urls.v2.sourcingEvents.viewAll, query: bucketQuery("evaluering"), roles: ["advisor"], badgeKey: "anskEvaluering" },
      { icon: FiAward, labelKey: "Award and standstill", path: urls.v2.sourcingEvents.viewAll, query: bucketQuery("tildeling"), roles: ["advisor"], badgeKey: "anskTildeling" },
      { icon: FiArchive, labelKey: "Completed", path: urls.v2.sourcingEvents.viewAll, query: bucketQuery("fullfort"), roles: ["advisor"], badgeKey: "anskFullfort" },
    ],
    action: { labelKey: "New procurement", roles: ["advisor"], kind: "createSourcingEvent" },
  },
  {
    titleKey: "Contracts",
    items: [
      { icon: FiFolder, labelKey: "All contracts", path: urls.v2.contracts.viewAll, roles: ALL_ROLES },
      { icon: FiFileText, labelKey: "Active contracts", path: urls.v2.contracts.active, roles: ALL_ROLES },
    ],
    action: { labelKey: "New contract", roles: ALL_ROLES, kind: "createContract" },
  },
];
